<template>
  <main>
    <b-container class="py-4">
      <b-form class="d-flex flex-row flex-wrap align-items-end mb-3" @submit.prevent="fetchSaleAttrs">
        <b-form-group class="flex-shrink-0 mb-0 mr-3" label="Chain">
          <b-form-select :options="chainOptions" v-model="chainId" required :disabled="isFormLocked"></b-form-select>
        </b-form-group>
        <b-form-group class="flex-grow-1 mb-0 mr-3" style="min-width: 360px;" label="Sale contract address">
          <b-form-input type="text" class="text-monospace" v-model="saleContractAddress" required :disabled="isFormLocked" />
        </b-form-group>
        <div>
          <b-button type="submit" class="flex-shrink-0" variant="primary" :disabled="isFormLocked">Load data</b-button>
        </div>
      </b-form>
      <b-table-lite :items="saleContractAttrEntries" small />

      <b-form @submit.prevent="startSpamPurchase">
        <b-form-group>
          <div>
            <label>Wallet</label>
            ·
            <router-link to="/private-key-wallets" class="p-0">Manage</router-link>
          </div>
          <b-form-select :options="privateKeyWallets" v-model="walletAddress" required :disabled="isFormLocked" />
        </b-form-group>
        <div class="text-secondary mb-3">
          <div>Balance: {{ displayBalance }}</div>
          <div>Allowance: {{ displayAllowance }}</div>
          <div>Whitelist Allocation: {{ displayAllocation }}</div>
          <div v-if="needApprove">
            Need approval
            <b-button variant="primary" @click="sendApprove">Send approve</b-button>
          </div>
        </div>
        <b-form-row>
          <b-form-group class="col-4" :label="'Input asset ' + assetSymbol">
            <b-input-group>
              <b-form-input type="number" class="text-monospace" v-model="inputAssetAmount" min="10" :step="1e-6" required />
            </b-input-group>
          </b-form-group>
          <b-form-group class="col-4" label="Gas price gwei">
            <b-input-group>
              <b-form-input type="number" class="text-monospace" v-model="gasPriceGwei" min="0.1" max="100" step="0.1" required />
            </b-input-group>
          </b-form-group>
          <b-form-group class="col-4" label="Spam interval (ms)">
            <b-input-group>
              <b-form-input type="number" class="text-monospace" v-model="spamInterval" min="1" max="5000" step="1" required />
            </b-input-group>
          </b-form-group>
        </b-form-row>
        <div>
          <div v-if="spamStarted">
            <div>Open console to see activity</div>
            <b-button type="button" @click="stopSpamPurchase">Stop</b-button>
          </div>
          <b-button v-else type="submit" class="flex-shrink-0" variant="primary">Spam purchase tx</b-button>
        </div>
      </b-form>

    </b-container>
  </main>
</template>

<style scoped lang="scss">

</style>

<script lang="ts">
import _ from "lodash";
import { DateTime } from "luxon";
import BigNumber from "bignumber.js";

import * as utils from "@/utils.ts";
import * as constants from "@/constants.ts";
import * as privateKeyWalletService from "@/services/privateKeyWalletService.ts";
import * as web3Service from "@/services/web3Service.ts";

import {ethers} from "ethers";
import holdstationSaleAbi from "@/services/abi/holdstationSaleAbi.json";
import erc20Abi from "@/services/abi/erc20Abi.json";

export default {
  name: "HoldstationIdo",

  data() {
    return {
      privateKeyWallets: [],
      walletAddress: "",

      chainId: 56,
      chainOptions: [
        constants.CHAIN_ID_ETH,
        constants.CHAIN_ID_BSC,
        constants.CHAIN_ID_POLYGON,
        constants.CHAIN_ID_ARBITRUM,
        constants.CHAIN_ID_OPTIMISM,
        constants.CHAIN_ID_BASE,
        constants.CHAIN_ID_BERA,
      ].map(chainId => ({ value: chainId, text: constants.chainNames[chainId] })),
      saleContractAddress: "",
      saleContractAttrs: null,
      isFormLocked: false,

      saleProps: {},

      assetSymbol: "USDT",
      assetDecimals: 18,

      displayBalance: "0",
      displayAllowance: "0",
      displayAllocation: "0",
      needApprove: true,
      inputAssetAmount: "",

      gasPriceGwei: "1",
      spamInterval: "500",
      spamStarted: false,
      spamIntervalHandler: null,
    };
  },

  computed: {
    saleContractAttrEntries() {
      return this.saleContractAttrs ?
        Object.keys(this.saleContractAttrs).map(k => ({ key: k, value: this.saleContractAttrs[k].toString() })) :
        [];
    }
  },

  mounted() {
    document.title = "Holdstation Ido";
    this.privateKeyWallets = privateKeyWalletService.getWallets().filter(w => w.type === "evm").map(w => {
      let text = w.address;
      if (w.label) {
        text = `${w.label} (${w.address})`;
      }
      return { text, value: w.address };
    });
    this.walletAddress = this.privateKeyWallets[0]?.value;
    this.chainId = +this.$route.query.chainId;
    this.saleContractAddress = this.$route.query.saleContractAddress;

    this.fetchSaleAttrs();
    setInterval(() => {
      if (this.walletAddress && this.chainId && this.saleContractAddress) {
        this.fetchSaleAttrs();
      }
    }, 3000);
  },

  methods: {
    async fetchSaleAttrs() {
      try {
        const saleContractAttrs = await web3Service.getContractAttrs(this.chainId, this.saleContractAddress, holdstationSaleAbi);
        // console.log(saleContractAttrs);
        this.saleContractAttrs = saleContractAttrs;

        const inputTokenMetadata = await web3Service.getEvmTokenMetadata(this.chainId, saleContractAttrs.sourceToken);
        this.assetSymbol = inputTokenMetadata.symbol;
        this.assetDecimals = inputTokenMetadata.decimals;

        const {allowance, balance} = (await web3Service.getBalancesAndAllowances(this.chainId, this.walletAddress, this.saleContractAddress, [saleContractAttrs.sourceToken]))[saleContractAttrs.sourceToken.toLowerCase()];
        this.needApprove = BigNumber(allowance.toString()).shiftedBy(-inputTokenMetadata.decimals).lte(20000);

        this.displayAllowance = BigNumber(allowance.toString()).shiftedBy(-inputTokenMetadata.decimals).toFixed();
        this.displayBalance = BigNumber(balance.toString()).shiftedBy(-inputTokenMetadata.decimals).toFixed();

        const provider = await web3Service.getProviderForChain(this.chainId);
        const saleContract = new ethers.Contract(this.saleContractAddress, holdstationSaleAbi, provider);
        const allocation = await saleContract.tier1Whitelists.staticCall(this.walletAddress);
        // const allocation = await saleContract.tier1Whitelists.staticCall("0xf57558364A793096F6a9BC122270B2A7Ef887B5E");
        this.displayAllocation = BigNumber(allocation.toString()).shiftedBy(-inputTokenMetadata.decimals).toFixed();
        if (!this.inputAssetAmount) {
          this.inputAssetAmount = this.displayAllocation;
        }

      } catch (e) {
        console.error(e);
      }
    },

    async sendApprove() {
      const provider = await web3Service.getProviderForChain(this.chainId);
      const privateKeyWallet = privateKeyWalletService.getWallets().find(w =>
        w.type === "evm" &&
        w.address.toLowerCase() === this.walletAddress.toLowerCase()
      );
      const wallet = new ethers.Wallet(privateKeyWallet.privateKey, provider);

      const erc20Interface = new ethers.Interface(erc20Abi);
      const assetTokenContract = new ethers.Contract(this.saleContractAttrs.sourceToken, erc20Interface, wallet);

      const txRequest = await assetTokenContract.approve.populateTransaction(this.saleContractAddress, ethers.MaxUint256);
      txRequest.from = wallet.address;
      txRequest.gasPrice = ethers.parseUnits("0.1", "gwei");
      const estimatedGas = await provider.estimateGas(txRequest);
      txRequest.gasLimit = BigInt(BigNumber(estimatedGas.toString()).multipliedBy(1.5).decimalPlaces(0).toFixed());

      const tx = await wallet.sendTransaction(txRequest);
      console.log("approve tx", utils.getExplorerUrl(this.chainId, "tx", tx.hash));
    },

    async startSpamPurchase() {
      if (!this.spamStarted) {
        this.spamStarted = true;
        this.spamIntervalHandler = setInterval(async () => {
          const provider = await web3Service.getProviderForChain(this.chainId);
          const privateKeyWallet = privateKeyWalletService.getWallets().find(w =>
            w.type === "evm" &&
            w.address.toLowerCase() === this.walletAddress.toLowerCase()
          );
          const wallet = new ethers.Wallet(privateKeyWallet.privateKey, provider);

          const saleContract = new ethers.Contract(this.saleContractAddress, holdstationSaleAbi, wallet);
          const amountParam = BigNumber(this.inputAssetAmount).shiftedBy(this.assetDecimals).toFixed();

          const txRequest = await saleContract.privatePurchase.populateTransaction(amountParam);
          txRequest.from = wallet.address;
          txRequest.gasPrice = ethers.parseUnits(this.gasPriceGwei, "gwei");
          const estimatedGas = await provider.estimateGas(txRequest);
          txRequest.gasLimit = BigInt(BigNumber(estimatedGas.toString()).multipliedBy(1.5).decimalPlaces(0).toFixed());

          const tx = await wallet.sendTransaction(txRequest);
          console.log("purchase tx", utils.getExplorerUrl(this.chainId, "tx", tx.hash));
        }, +this.spamInterval);
      }
    },

    async stopSpamPurchase() {
      clearInterval(this.spamIntervalHandler);
      this.spamStarted = false;
    }
  },

};
</script>
